import React, { useState, useEffect } from "react";
import {
    BrowserRouter as Router,
    Route,
    Routes,
    Navigate,
} from "react-router-dom";
import Navbar from "./components/navbar/navbar";
import Sidebar from "./components/sidebar/sidebar";
import Footer from "./components/footer/footer";
import Login from "./pages/login/login";
import DefinirPassword from "./pages/login/definirPassword";
import MobiliarioCentral from "./pages/mobiliarioCentral/mobiliarioCentral";
import { initializeApi, getApiInstance } from "./utils/api";
import Dashboard from "./pages/dashboard/dashboard";
import Muebles from "./pages/dashboard/gestionMuebles/gestionMuebles";
import DetalleMueble from "./pages/detalleMueble/detalleMueble";

import "./App.scss";
import MueblesSueltos from "./pages/mueblesSueltos/mueblesSueltos";
import MaestroDotaciones from "./pages/maestroDotaciones/maestroDotaciones";
import Versiones from "./pages/dashboard/versiones/versiones";
import { BorradoresProvider } from "./utils/borradoresContext";
import PerfilUsuario from "./pages/perfilUsuario/perfilUsuario";
import Error500 from "./pages/httpErrors/error500/error500";
import Error404 from "./pages/httpErrors/error404/error404";
import Mesas from "./pages/mesas/mesas";
import MobiliarioPerimetral from "./pages/mobiliarioPerimetral/mobiliarioPerimetral";
import Probadores from "./pages/probadores/probadores";
import Caja from "./pages/caja/caja";
import Instalaciones from "./pages/instalaciones/instalaciones";
import Senaletica from "./pages/senaletica/senaletica";
import Boh from "./pages/boh/boh";
import Versions from "./pages/versions/versions";
import GestionMoneda from "./pages/dashboard/gestionMoneda/gestionMoneda";
import GestionUsuarios from "./pages/dashboard/usuarios/gestionUsuarios";
import GestionTarifas from "./pages/dashboard/gestionTarifas/gestionTarifas";
import GestionTarifasProveedor from "./pages/gestionTarifasProveedor/gestionTarifasProveedor";
import DotacionesFijas from "./pages/dotacionesFijas/dotacionesFijas";
import Descatalogados from "./pages/dashboard/descatalogados/descatalogados";
import GestionPresupuestoProveedor from "./pages/gestionPresupuestoProveedor/gestionPresupuestoProveedor";
import PresupuestosBim5d from "./pages/dashboard/presupuestosBim5d/presupuestosBim5d";
import PresupuestosMC from "./pages/dashboard/presupuestosMC/presupuestosMC";
import Ratios from "./pages/ratios/ratios";
import DetallesConstructivos from "./pages/detallesConstructivos/detallesConstructivos";
import DetallesInstalaciones from "./pages/detallesInstalaciones/detallesInstalaciones";
import MontajeMobiliario from "./pages/montajeMobiliario/montajeMobiliario";
import Obras from "./pages/dashboard/obras/obras";
import Error403 from "./pages/httpErrors/error403/error403";
import { canAccessObras, obrasRole } from "./utils/obrasAccess";

// Solo desarrollo: /obras-mock con la misma estructura que la app autenticada
// (barra superior de 5.6rem, lateral y <main class="app__content">), para que
// alturas y scrolls se comporten igual que en /admin/obras. ?sidebar=abierto usa
// el lateral desplegado (26rem); por defecto, plegado (6rem).
function ObrasMockShell() {
    const sidebarOpen = new URLSearchParams(window.location.search).get("sidebar") === "abierto";
    return (
        <>
            <div style={{ height: "5.6rem", flex: "0 0 auto", background: "#102a43" }} aria-hidden="true" />
            <div className="app__container">
                <div style={{ width: sidebarOpen ? "26rem" : "6rem", flex: "0 0 auto", background: "#1f3a5f" }} aria-hidden="true" />
                <main className="app__content">
                    {/* En desarrollo, ?rol=imagen|estudio muestra la vista de esos perfiles. */}
                    <Obras role={new URLSearchParams(window.location.search).get("rol") || "total"} />
                </main>
            </div>
        </>
    );
}

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [user, setUser] = useState(null);

    useEffect(() => {
        const initialize = async () => {
            await initializeApi();
            const token = localStorage.getItem("token");

            if (token) {
                const decodedToken = JSON.parse(atob(token.split(".")[1]));

                // Verificar si el token ha expirado
                const currentTime = Math.floor(Date.now() / 1000);
                if (decodedToken.exp && decodedToken.exp < currentTime) {
                    logoutUser();
                    return;
                }

                setIsAuthenticated(true);
                setUserRole(
                    decodedToken[
                        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                    ]
                );
                fetchUserData();
            }
        };
        initialize();
    }, []);

    const fetchUserData = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get("/api/Usuario/GetCurrentUser");

            setUser(response.data);
        } catch (error) {
            console.error("Error fetching user data:", error);
        }
    };

    const authenticateUser = (token) => {
        localStorage.setItem("token", token);
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setIsAuthenticated(true);
        setUserRole(
            decodedToken[
                "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
            ]
        );
        fetchUserData();
    };

    const logoutUser = () => {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setUserRole(null);
        setUser(null);
    };

    return (
        <Router>
            <div className="app">
                {!isAuthenticated ? (
                    <Routes>
                        {process.env.NODE_ENV === "development" && <Route path="/obras-mock" element={<ObrasMockShell />} />}
                        {/* Si no está autenticado, cualquier ruta redirige a login */}
                        <Route path="/*" element={<Navigate to="/login" />} />
                        <Route
                            path="/login"
                            element={<Login onLogin={authenticateUser} />}
                        />
                        <Route
                            path="/definir-password"
                            element={<DefinirPassword />}
                        />
                    </Routes>
                ) : (
                    <BorradoresProvider>
                        <Navbar user={user} onLogout={logoutUser} />
                        <div className="app__container">
                            <Sidebar userRole={userRole} user={user} />
                            <main className="app__content">
                                <Routes>
                                    {/* Ruta Home */}
                                    <Route
                                        path="/"
                                        element={
                                            <Navigate to="/mobiliario-central/todo" />
                                        }
                                    />

                                    {/* Redirigir /login si ya estás autenticado */}
                                    <Route
                                        path="/login"
                                        element={<Navigate to="/" />}
                                    />

                                    {/* Enlace de restablecer contraseña abierto con la sesión iniciada */}
                                    <Route
                                        path="/definir-password"
                                        element={<DefinirPassword />}
                                    />

                                    {/* RUTAS DASHBOARD */}
                                    {userRole === "Admin" && (
                                        <>
                                            <Route
                                                path="/admin"
                                                element={
                                                    <Dashboard user={user} />
                                                }
                                            />
                                            <Route
                                                path="/admin/usuarios"
                                                element={<GestionUsuarios />}
                                            />
                                            <Route
                                                path="/admin/muebles"
                                                element={<Muebles />}
                                            />
                                            {/* TODO: Implementar en futuras versiones  */}
                                            {/* <Route path="/admin/informes" element={<Borradores />} /> */}
                                            {/* <Route path="/admin/comentarios" element={<Borradores />} /> */}
                                            <Route
                                                path="/admin/versiones"
                                                element={<Versiones />}
                                            />
                                            <Route
                                                path="/admin/maestro-dotaciones"
                                                element={<MaestroDotaciones />}
                                            />
                                            <Route
                                                path="/admin/gestion-moneda"
                                                element={<GestionMoneda />}
                                            />

                                            <Route
                                                path="/admin/muebles-descatalogados"
                                                element={<Descatalogados />}
                                            />
                                            <Route
                                                path="/admin/gestion-tarifas"
                                                element={
                                                    <GestionTarifas
                                                        user={user}
                                                    />
                                                }
                                            />
                                            {user !== null &&
                                                user.codTipoEmpresa !== 1 &&
                                                user.codTipoEmpresa !== 2 && (
                                                    <Route
                                                        path="/admin/solicitudes-tarifas"
                                                        element={
                                                            <GestionTarifasProveedor
                                                                user={user}
                                                            />
                                                        }
                                                    />
                                                )}
                                                <Route
                                                path="/admin/presupuestos"
                                                element={
                                                    <GestionPresupuestoProveedor
                                                        user={user}
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/admin/presupuestos-bim-5d"
                                                element={<PresupuestosBim5d />}
                                            />
                                            <Route
                                                path="/admin/ratios"
                                                element={<Ratios />}
                                            />
                                            <Route
                                                path="/admin/presupuestos-mc"
                                                element={<PresupuestosMC />}
                                            />
                                        </>
                                    )}

                                    <Route
                                        path="/admin/obras"
                                        element={user === null
                                            ? <p role="status">Cargando usuario…</p>
                                            : canAccessObras(user) ? <Obras role={obrasRole(user)} /> : <Error403 />}
                                    />

                                    <Route
                                        path="/mobiliario-central/:subcategoria"
                                        element={<MobiliarioCentral />}
                                    />
                                    <Route
                                        path="/mobiliario-perimetral/:subcategoria"
                                        element={<MobiliarioPerimetral />}
                                    />
                                    <Route
                                        path="/mesas/:subcategoria"
                                        element={<Mesas />}
                                    />
                                    <Route
                                        path="/probadores/:subcategoria"
                                        element={<Probadores />}
                                    />
                                    <Route
                                        path="/caja/:subcategoria"
                                        element={<Caja />}
                                    />
                                    <Route
                                        path="/instalaciones/:subcategoria"
                                        element={<Instalaciones />}
                                    />
                                    <Route
                                        path="/senaletica/:subcategoria"
                                        element={<Senaletica />}
                                    />
                                    <Route
                                        path="/boh/:subcategoria"
                                        element={<Boh />}
                                    />
                                    <Route
                                        path="/dotaciones-fijas/:subcategoria"
                                        element={<DotacionesFijas />}
                                    />
                                    <Route
                                        path="/detalles-constructivos/:subcategoria"
                                        element={<DetallesConstructivos />}
                                    />
                                    <Route
                                        path="/detalles-instalaciones/:subcategoria"
                                        element={<DetallesInstalaciones />}
                                    />
                                    <Route
                                        path="/montaje-mobiliario/:subcategoria"
                                        element={<MontajeMobiliario />}
                                    />
                                    <Route
                                        path="/perfil"
                                        element={<PerfilUsuario />}
                                    />

                                    {/* Otras rutas */}
                                    <Route
                                        path="/dotacion/lista-dotaciones"
                                        element={<MueblesSueltos />}
                                    />
                                    <Route
                                        path="/dotacion/:subcategoria"
                                        element={<MueblesSueltos />}
                                    />
                                    <Route
                                        path="/ficha-producto/:codMueble"
                                        element={<DetalleMueble user={user} />}
                                    />
                                    <Route
                                        path="/dotacion/maestro-dotaciones/dotaciones"
                                        element={<MaestroDotaciones />}
                                    />

                                    <Route
                                        path="/versions"
                                        element={<Versions />}
                                    />

                                    {/* Manejo de errores */}
                                    <Route path="/500" element={<Error500 />} />

                                    {/* Ruta 404 para páginas inexistentes */}
                                    <Route path="*" element={<Error404 />} />
                                </Routes>
                            </main>
                        </div>
                        <Footer />
                    </BorradoresProvider>
                )}
            </div>
        </Router>
    );
}

export default App;
